// ─────────────────────────────────────────────────────────────
// LoopBuilderCategoriesView — CRUD for Loop Builder categories.
// 1:1 with the song-side CategoriesView: list + add/edit form,
// duplicate-name guard, usage-count badge, immediate D1 push on
// save/delete. Differs only in: no colour pickers/dots.
// ─────────────────────────────────────────────────────────────

import {createElement} from "@opendaw/lib-jsx"
import {Lifecycle} from "@opendaw/lib-std"
import {STApi} from "@/jerry/st-panel/STApi"
import {STCategory} from "@/jerry/st-panel/STTypes"
import {confirmDelete} from "@/jerry/st-panel/views/ConfirmDeleteDialog"

export const LoopBuilderCategoriesView = ({}: {lifecycle: Lifecycle}) => {
    let categories: STCategory[] = STApi.getLoopBuilderCategories()
    let editingId: string | null = null
    let usageCounts: Record<string, number> = {}

    const listEl: HTMLElement = <div/>
    const nameInput: HTMLInputElement = (
        <input className="st-input" type="text" placeholder="Category name…" style={{flex:"1"}}/>
    )
    const saveBtn: HTMLElement = (
        <div className="st-btn primary" style={{flexShrink:"0"}}>Add</div>
    )
    const cancelBtn: HTMLElement = (
        <div className="st-btn" style={{flexShrink:"0", display:"none"}}>Cancel</div>
    )
    const nameError: HTMLElement = (
        <div style={{
            display:"none", fontSize:"0.6rem", color:"var(--color-red)",
            marginTop:"0.25rem", marginBottom:"0.2rem",
        }}>A category with this name already exists.</div>
    )

    const resetForm = () => {
        editingId = null
        nameInput.value = ""
        saveBtn.textContent = "Add"
        cancelBtn.style.display = "none"
    }

    const renderList = () => {
        listEl.innerHTML = ""
        if (categories.length === 0) {
            listEl.appendChild(
                <div className="st-empty">No categories yet.<br/>Add one below.</div>
            )
            return
        }
        for (const cat of categories) {
            const count = usageCounts[cat.id] ?? 0
            const countBadge: HTMLElement = (
                <span style={{
                    fontSize:"9px", fontWeight:"600", letterSpacing:"0.04em",
                    padding:"1px 5px", borderRadius:"3px", flexShrink:"0",
                    background:"rgba(255,255,255,0.07)", color:"rgba(255,255,255,0.35)",
                }}>{count} {count === 1 ? "loop" : "loops"}</span>
            )
            const item: HTMLElement = (
                <div className="st-crud-item">
                    <span className="st-crud-name">{cat.name}</span>
                    {countBadge}
                    <div className="st-crud-actions">
                        <div className="st-btn" data-id={cat.id} data-action="edit" style={{padding:"0.2rem 0.5rem"}}>Edit</div>
                        <div className="st-btn danger" data-id={cat.id} data-action="del" style={{padding:"0.2rem 0.5rem"}}>✕</div>
                    </div>
                </div>
            )
            listEl.appendChild(item)
        }
        listEl.querySelectorAll<HTMLElement>("[data-action='edit']").forEach(btn => {
            btn.addEventListener("click", () => {
                const id = btn.getAttribute("data-id")!
                const cat = categories.find(entry => entry.id === id)
                if (!cat) return
                editingId = id
                nameInput.value = cat.name
                saveBtn.textContent = "Save"
                cancelBtn.style.display = "inline-flex"
                nameInput.focus()
            })
        })
        listEl.querySelectorAll<HTMLElement>("[data-action='del']").forEach(btn => {
            btn.addEventListener("click", async () => {
                const id = btn.getAttribute("data-id")!
                const cat = categories.find(entry => entry.id === id)
                if (!cat) return
                const confirmed = await confirmDelete({name: cat.name, kind: "category"})
                if (!confirmed) return
                await STApi.deleteLoopBuilderCategory(id)
                categories = STApi.getLoopBuilderCategories()
                if (editingId === id) resetForm()
                renderList()
            })
        })
    }

    saveBtn.addEventListener("click", async () => {
        const name = nameInput.value.trim()
        if (!name) return
        const duplicate = STApi.getLoopBuilderCategories().find(entry =>
            entry.name.toLowerCase() === name.toLowerCase() && entry.id !== editingId
        )
        if (duplicate) {
            nameError.style.display = "block"
            nameInput.focus()
            return
        }
        nameError.style.display = "none"
        let savedCat: STCategory
        if (editingId) {
            STApi.updateLoopBuilderCategory(editingId, {name})
            savedCat = STApi.getLoopBuilderCategories().find(entry => entry.id === editingId)!
        } else {
            savedCat = STApi.createLoopBuilderCategory(name, "#5cb8bf")
        }
        try { await STApi.upsertLoopBuilderCategory(savedCat) }
        catch (e) { console.warn("[LoopBuilderCategoriesView] upsert failed:", e) }
        categories = STApi.getLoopBuilderCategories()
        resetForm()
        renderList()
    })
    nameInput.addEventListener("input", () => { nameError.style.display = "none" })
    cancelBtn.addEventListener("click", resetForm)

    STApi.getLoopBuilderUsageCounts()
        .then(counts => { usageCounts = counts.categories; renderList() })
        .catch(() => { /* non-fatal — counts stay 0 */ })

    renderList()

    return (
        <div>
            {listEl}
            <div className="st-section" style={{borderTop:"1px solid rgba(255,255,255,0.06)"}}>
                <div className="st-label" style={{marginBottom:"0.5rem"}}>Add / Edit Category</div>
                <div className="st-row" style={{marginBottom:"0.1rem"}}>
                    {nameInput}
                </div>
                {nameError}
                <div className="st-row">
                    {saveBtn}
                    {cancelBtn}
                </div>
            </div>
        </div>
    )
}
